import React, { useState, memo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { FaGavel, FaBookOpen, FaLightbulb } from 'react-icons/fa';
import '../styles/SpeakerPage.css';

// --- DATA & CONSTANTS ---
const engagementsData = [
    { role: 'Speaker', event: 'Virtual Reality; Competent and Diligent Representation in the Digital Age, Hill Country CASA Ad Litem Seminar', date: 'February 2025' },
    { role: 'Speaker/Faculty', event: 'How to Engage Father’s in Child Welfare Cases, Child Welfare Judges Conference', date: 'October 2024' },
    { role: 'Speaker', event: 'Child Protection Cases, What Clerks Need to Know, Texas District Clerk’s Association', date: 'Date TBC' },
    { role: 'Faculty', event: 'Trial Skills Training, Texas Children’s Commission', date: 'September 2024' },
    { role: 'Speaker/Moderator', event: 'Child Welfare Judges Conference', date: 'October 2023' },
    { role: 'Speaker', event: 'Case Law Update, Child Protection Court Convening', date: 'July, 2023' },
    { role: 'Speaker', event: 'Case Law Update, Texas District and County Attorneys Association, Prosecuting Crimes Against Children Conference', date: 'April, 2023' },
    { role: 'Speaker', event: 'Roles and Duties of Attorneys Ad Litem, Hill Country CASA Ad Litem Seminar', date: 'February 2023' },
    { role: 'Faculty', event: 'Texas Advanced Family Law, Child Protection Court Track, Reinstatement of Parental Rights', date: 'August 2022' },
    { role: 'Speaker', event: 'Reinstatement of Parental Rights, Hill Country CASA Ad Litem Seminar', date: 'February 2022' },
    { role: 'Speaker', event: 'Innovative Practices in Child Protection Courts, Supreme Court of Texas Children’s Commission', date: 'July 2021' },
    { role: 'Speaker', event: 'COVID Practice and Remote Court, Hill Country CASA Ad Litem Seminar', date: 'February 2021' },
    { role: 'Speaker', event: 'Texas District Clerk’s Association iDocket Webinar', date: 'Summer 2020' },
    { role: 'Speaker', event: 'Provided legal training to Hill Country CASA Volunteers', date: '2017' },
    { role: 'Speaker', event: 'Panel Member at Hill Country CASA Ad Litem Seminar', date: '2016' },
];
const topicsData = [
  { icon: <FaBookOpen />, title: "Literacy & Storytelling", description: "Exploring the profound impact of reading and the craft of building compelling narratives." },
  { icon: <FaGavel />, title: "The Law & Justice", description: "Providing insightful analysis of the legal system, child welfare, and the pursuit of justice." },
  { icon: <FaLightbulb />, title: "Motivation & Achievement", description: "Inspiring audiences to overcome obstacles and achieve their most ambitious goals." },
];
const tickerData = ["Speaker", "Faculty", "Writer"]; // Only the 3 tags

// --- SEO CONSTANTS (Unchanged) ---
const pageUrl = "https://robertfalkenberg.com/speaker";
const pageTitle = "Robert Falkenberg | Professional Speaker - Engagements & Booking";
const pageDescription = "Book Robert Falkenberg, an engaging public speaker with 20+ years of experience in literacy, law, and motivation for your next corporate event, seminar, or school function.";
const ogImageUrl = `${pageUrl}/assets/speaker-og-image.webp`;
const jsonLdData = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Robert Falkenberg",
    "url": pageUrl,
    "image": ogImageUrl,
    "jobTitle": "Author and Public Speaker",
    "description": pageDescription,
    "knowsAbout": ["Law", "Child Welfare", "Literacy", "Creative Writing", "Motivation"]
};

// --- LAZY LOADING WRAPPER (Unchanged) ---
const LazyLoadSection = ({ children, placeholderHeight = '400px' }) => {
    const [isInView, setIsInView] = useState(false);
    return (
        <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            onViewportEnter={() => setIsInView(true)}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.7 }}
            style={{ minHeight: !isInView ? placeholderHeight : 'auto' }}
        >
            {isInView && children}
        </motion.div>
    );
};

// --- Timeline Component (Adjusted Dot Alignment) ---
const TimelineSection = ({ engagementsData }) => {
  const [showAllEngagements, setShowAllEngagements] = useState(false);
  const timelineRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: timelineRef,
    offset: ["start center", "end end"]
  });
  const engagementsToDisplay = showAllEngagements ? engagementsData : engagementsData.slice(0, 6);
  const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } };
  const itemVariants = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } } };

  return (
    <motion.section
      ref={timelineRef}
      variants={containerVariants}
    >
      <motion.h2 className="speaker-sectionTitle" variants={itemVariants}>Past Engagements</motion.h2>
      <div className="speaker-timeline">
        <div className="speaker-timeline-background-line" />
        <motion.div
          className="speaker-timeline-progress-line"
          style={{ scaleY: scrollYProgress }}
        />
        <AnimatePresence>
          {engagementsToDisplay.map((item) => (
            <motion.div
              key={item.event}
              className="speaker-timelineEntry"
              variants={itemVariants}
              layout
            >
              <div className="speaker-timeline-dot-wrapper"> {/* New wrapper for dot alignment */}
                <div className="speaker-timeline-dot"></div>
              </div>
              <div className="speaker-timeline-content">
                <span className="speaker-timelineBadge">{item.role}</span>
                <p className="speaker-timelineEvent">{item.event}</p>
                <p className="speaker-timelineDate">{item.date}</p>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
      {!showAllEngagements && engagementsData.length > 6 && (
        <motion.div className="speaker-showMore-wrap" initial={{opacity:0}} animate={{opacity:1}}>
          <button onClick={() => setShowAllEngagements(true)} className="speaker-showMore-btn" aria-label="Show all past speaking engagements">
            Show All ({engagementsData.length}) Engagements
          </button>
        </motion.div>
      )}
    </motion.section>
  );
};

// --- MAIN PAGE COMPONENT ---
function SpeakerPageComponent() {
  const { scrollYProgress: heroScrollYProgress } = useScroll();
  const heroBgScale = useTransform(heroScrollYProgress, [0, 0.5], [1, 1.15]);

  const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } };
  const itemVariants = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } } };
  const titleVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.08 } } };
  const letterVariants = { hidden: { y: "100%" }, visible: { y: 0, transition: { duration: 0.8, ease: 'easeOut' } } };

  return (
    <div className="speaker-pageWrapper">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={ogImageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImageUrl} />
        <script type="application/ld+json">{JSON.stringify(jsonLdData)}</script>
      </Helmet>

      <header className="speaker-heroContainer">
        <motion.div className="speaker-heroBackground" style={{ scale: heroBgScale }} />
        <motion.h1 className="speaker-heroTitle" variants={titleVariants} initial="hidden" animate="visible">
          {"Public Speaker".split(' ').map((word, wc) => (
            <span key={wc} className="speaker-heroTitleWord">
              <motion.span variants={letterVariants}>{word}</motion.span>
            </span>
          ))}
        </motion.h1>
        <motion.p className="speaker-heroSubtitle" initial={{opacity: 0, y: 20}} animate={{opacity: 1, y: 0, transition:{delay: 0.5, duration: 0.8}}}>
          Engaging Minds, Inspiring Action.
        </motion.p>
      </header>

      <main className='speaker-mainContent'>
        <LazyLoadSection>
            <motion.section variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                <motion.h2 className="speaker-sectionTitle" variants={itemVariants}>Speaking Topics</motion.h2>
                <div className="speaker-bentoGrid">
                    {topicsData.map((topic, i) => (
                    <motion.div key={i} className="speaker-bentoCard" variants={itemVariants} whileHover={{y: -5}}>
                        <div className="speaker-bentoCard-icon">{topic.icon}</div>
                        <h3 className="speaker-bentoCard-title">{topic.title}</h3>
                        <p className="speaker-bentoCard-description">{topic.description}</p>
                    </motion.div>
                    ))}
                </div>
            </motion.section>
        </LazyLoadSection>

        {/* --- REINTRODUCED: Animated Ticker --- */}
        <LazyLoadSection placeholderHeight="80px">
          <section>
            <div className="speaker-ticker-wrap">
              <motion.div
                className="speaker-ticker-track"
                animate={{ x: ['0%', '-100%'] }} // Animate to -100% to slide all the way through
                transition={{ ease: 'linear', duration: 60, repeat: Infinity }} // Adjust duration for speed
                style={{ willChange: 'transform' }}
              >
                {[...tickerData, ...tickerData].map((item, i) => ( // Duplicate for smoother continuous effect
                  <span key={i} className="speaker-ticker-item">{item}</span>
                ))}
              </motion.div>
            </div>
          </section>
        </LazyLoadSection>

        {/* --- UPDATED: Timeline Section --- */}
        <LazyLoadSection>
          <TimelineSection engagementsData={engagementsData} />
        </LazyLoadSection>

        <LazyLoadSection placeholderHeight="300px">
            <motion.section className="speaker-cta-section" initial={{opacity: 0}} whileInView={{opacity: 1}} viewport={{ once: true }} transition={{delay: 0.2}}>
                <h2 className="speaker-sectionTitle">Ready to Inspire?</h2>
                <p className="speaker-cta-text">Connect today to discuss your needs and how Robert can contribute to the success of your next event.</p>
                <a href="mailto:robertfalkenbergauthor@gmail.com" className="speaker-cta-btn">
                    Book Robert Falkenberg
                </a>
            </motion.section>
        </LazyLoadSection>
      </main>
    </div>
  );
};

export default memo(SpeakerPageComponent);